import React, { useEffect, useRef } from 'react';
import {
  IconButton,
  IconButtonProps,
  Tooltip,
  Button,
  ButtonProps,
  useTheme,
  SxProps,
  Theme,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useClipboard } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';

export interface CopyButtonProps {
  /** The text content to copy to clipboard */
  text: string;
  /** Button variant - icon button or text button */
  variant?: 'icon' | 'button';
  /** Button size */
  size?: 'small' | 'medium' | 'large';
  /** Button color */
  color?:
    | 'primary'
    | 'secondary'
    | 'inherit'
    | 'default'
    | 'success'
    | 'error'
    | 'info'
    | 'warning';
  /** Custom tooltip text for default state */
  tooltip?: string;
  /** Custom tooltip text for success state */
  successTooltip?: string;
  /** Custom tooltip text for error state */
  errorTooltip?: string;
  /** Duration to show success state (ms) */
  successDuration?: number;
  /** Duration to show error state (ms) */
  errorDuration?: number;
  /** Button text for button variant */
  buttonText?: string;
  /** Button text for success state */
  successButtonText?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Custom className */
  className?: string;
  /** Custom styles */
  sx?: SxProps<Theme>;
  /** Callback fired when copy succeeds */
  onSuccess?: (text: string) => void;
  /** Callback fired when copy fails */
  onError?: (error: Error) => void;
  /** Test ID for automation */
  'data-testid'?: string;
}

export const CopyButton: React.ComponentType<CopyButtonProps> = ({
  text,
  variant = 'icon',
  size = 'medium',
  color = 'primary',
  tooltip,
  successTooltip,
  errorTooltip,
  successDuration = 2000,
  errorDuration = 3000,
  buttonText,
  successButtonText,
  disabled = false,
  className,
  sx,
  onSuccess,
  onError,
  'data-testid': dataTestId,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const clipboard = useClipboard({ timeout: successDuration });

  // Track error state separately since Mantine's hook only tracks copied state
  const [hasError, setHasError] = React.useState(false);
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup error timeout on unmount
  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  // Reset error state when copy state changes
  useEffect(() => {
    if (clipboard.copied && hasError) {
      setHasError(false);
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = null;
      }
    }
  }, [clipboard.copied, hasError]);

  const handleCopy = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (disabled || !text) return;

    // Clear any existing error timeout
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }
    setHasError(false);

    try {
      clipboard.copy(text);
      onSuccess?.(text);
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Copy failed');
      setHasError(true);
      onError?.(err);

      // Set timeout to clear error state
      errorTimeoutRef.current = setTimeout(() => {
        setHasError(false);
        errorTimeoutRef.current = null;
      }, errorDuration);
    }
  };

  const getIcon = () => {
    if (hasError) {
      return <ErrorIcon />;
    }
    if (clipboard.copied) {
      return <CheckIcon />;
    }
    return <CopyIcon />;
  };

  const getTooltipTitle = () => {
    if (hasError) {
      return errorTooltip || t('blox.components.copy_button.tooltip.error');
    }
    if (clipboard.copied) {
      return successTooltip || t('blox.components.copy_button.tooltip.success');
    }
    return tooltip || t('blox.components.copy_button.tooltip.default');
  };

  const getButtonText = () => {
    if (clipboard.copied && successButtonText) {
      return successButtonText;
    }
    if (clipboard.copied) {
      return t('blox.actions.copied');
    }
    return buttonText || t('blox.actions.copy.label');
  };

  const getIconButtonColor = (): IconButtonProps['color'] => {
    if (clipboard.copied) return 'success';
    if (hasError) return 'error';
    return color;
  };

  const getButtonColor = (): ButtonProps['color'] => {
    if (clipboard.copied) return 'success';
    if (hasError) return 'error';
    return color === 'default' ? 'primary' : color;
  };

  const commonProps = {
    onClick: handleCopy,
    disabled: disabled,
    size,
    'data-testid': dataTestId,
    className: classnames(className, {
      'blox-copy-button': true,
      'blox-copy-button--success': clipboard.copied,
      'blox-copy-button--error': hasError,
    }),
    sx: {
      transition: theme.transitions.create(['color', 'background-color'], {
        duration: theme.transitions.duration.short,
      }),
      ...sx,
    },
  };

  const renderButton = () => {
    if (variant === 'icon') {
      return (
        <IconButton
          {...commonProps}
          color={getIconButtonColor()}
        >
          {getIcon()}
        </IconButton>
      );
    }

    return (
      <Button
        {...commonProps}
        color={getButtonColor()}
        variant="outlined"
        startIcon={getIcon()}
      >
        {getButtonText()}
      </Button>
    );
  };

  return (
    <Tooltip
      title={getTooltipTitle()}
      placement="top"
      arrow
    >
      <span>{renderButton()}</span>
    </Tooltip>
  );
};
